import { UUID_RE } from "@/utils/generic";
import { isRecord } from "@/utils/isRecord";
import { plural } from "./approvalValues";

export interface EditedField {
  name: string;
  newValue: unknown;
  // The table a table node's ``table`` field is set to.
  table?: string;
}

export type WorkflowApprovalAction =
  // ``table``: the table a table node reads or writes, when its config names
  // one (not a ``:var[...]`` bound at run time). ``key``: the alias the rest
  // of the call refers to the new node by. ``config``: the settings it is
  // created with; ``after``: the handles of the steps it runs after, for a
  // new workflow, whose edges are not steps of their own.
  | {
      op: "add";
      nodeType: string;
      name: string;
      key?: string;
      table?: string;
      config: Record<string, unknown>;
      after: string[];
    }
  // ``newName``: the name the same call renames the node to.
  | { op: "edit"; handle: string; fields: EditedField[]; newName?: string }
  | { op: "remove"; handle: string }
  | { op: "rename"; handle: string; newName: string }
  // ``branch``: the routing branch or ``on_error`` the edge leaves from.
  | {
      op: "edge";
      change: "connect" | "disconnect";
      source: string;
      target: string;
      branch?: string;
    }
  | { op: "trigger"; triggerType: string };

export interface WorkflowApprovalPlan {
  tool: "build" | "edit_nodes" | "create_trigger";
  workflowId?: string;
  // The name a new workflow is built with.
  workflowName?: string;
  actions: WorkflowApprovalAction[];
}

type NodeSpec = { key?: string; type?: string; name?: string };

function nodeName(spec: NodeSpec): string {
  return spec.name ?? spec.key ?? spec.type ?? "node";
}

function configTable(config: unknown): string | undefined {
  const table = isRecord(config) ? config.table : undefined;
  return typeof table === "string" && UUID_RE.test(table) ? table : undefined;
}

function parseAddNodes(
  raw: unknown,
  edges: WorkflowApprovalAction[] = []
): WorkflowApprovalAction[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(isRecord).map((spec) => {
    const key = typeof spec.key === "string" ? spec.key : undefined;
    return {
      op: "add" as const,
      nodeType: typeof spec.type === "string" ? spec.type : "",
      name: nodeName(spec as NodeSpec),
      key,
      table: configTable(spec.config),
      config: isRecord(spec.config) ? spec.config : {},
      after: edges.flatMap((e) =>
        e.op === "edge" && key && e.target === key ? [e.source] : []
      ),
    };
  });
}

function parseEdges(
  raw: unknown,
  change: "connect" | "disconnect"
): WorkflowApprovalAction[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(isRecord).flatMap((edge) =>
    typeof edge.source === "string" && typeof edge.target === "string"
      ? [
          {
            op: "edge" as const,
            change,
            source: edge.source,
            target: edge.target,
            branch:
              typeof edge.source_handle === "string"
                ? edge.source_handle
                : undefined,
          },
        ]
      : []
  );
}

/**
 * Turn a genie workflow-editing tool's name + args into a render plan.
 * Returns null for any tool that isn't a known workflow editor so the card
 * falls back to its generic rendering.
 */
export function parseWorkflowApprovalPlan(
  toolName: string,
  input: unknown
): WorkflowApprovalPlan | null {
  const name = toolName.replace(/^noxus_/, "");
  const args = isRecord(input) ? input : {};
  const workflowId =
    typeof args.workflow_id === "string" ? args.workflow_id : undefined;

  if (name === "workflows_build") {
    return {
      tool: "build",
      workflowId,
      workflowName: typeof args.name === "string" ? args.name : undefined,
      actions: parseAddNodes(args.nodes, parseEdges(args.edges, "connect")),
    };
  }

  if (name === "workflows_edit_nodes") {
    const actions: WorkflowApprovalAction[] = [
      ...parseAddNodes(args.add_nodes),
    ];
    if (isRecord(args.set_config)) {
      for (const [handle, fields] of Object.entries(args.set_config)) {
        actions.push({
          op: "edit",
          handle,
          fields: isRecord(fields)
            ? Object.entries(fields).map(([name, newValue]) => ({
                name,
                newValue,
                table:
                  name === "table"
                    ? configTable({ table: newValue })
                    : undefined,
              }))
            : [],
        });
      }
    }
    if (isRecord(args.rename_nodes)) {
      for (const [handle, newName] of Object.entries(args.rename_nodes)) {
        if (typeof newName !== "string") continue;
        // A rename of a node the call also edits is one step: its label set
        // to the same new name is that rename, not a second change.
        const edit = actions.find(
          (a): a is Extract<WorkflowApprovalAction, { op: "edit" }> =>
            a.op === "edit" && a.handle === handle
        );
        if (edit) {
          edit.newName = newName;
          edit.fields = edit.fields.filter(
            (f) => !(f.name === "label" && f.newValue === newName)
          );
        } else actions.push({ op: "rename", handle, newName });
      }
    }
    if (Array.isArray(args.remove_nodes)) {
      for (const handle of args.remove_nodes) {
        if (typeof handle === "string") actions.push({ op: "remove", handle });
      }
    }
    actions.push(
      ...parseEdges(args.add_edges, "connect"),
      ...parseEdges(args.remove_edges, "disconnect")
    );
    return { tool: "edit_nodes", workflowId, actions };
  }

  if (name === "workflows_create_trigger") {
    const def = isRecord(args.definition) ? args.definition : {};
    return {
      tool: "create_trigger",
      workflowId,
      actions: [
        {
          op: "trigger",
          triggerType: typeof def.type === "string" ? def.type : "trigger",
        },
      ],
    };
  }

  return null;
}

// The card's line under its title: the new workflow's name, and its steps.
export function workflowPlanSummary(plan: WorkflowApprovalPlan): string | null {
  const steps =
    plan.actions.length > 0 ? plural(plan.actions.length, "step") : null;
  return [plan.workflowName, steps].filter(Boolean).join(" · ") || null;
}
